import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn';

/** Dense data table. Wraps in a horizontal scroller so narrow screens don't break. */
export function Table({ className, children, ...rest }: ComponentProps<'table'>) {
  return (
    <div className="overflow-x-auto">
      <table {...rest} className={cn('w-full border-collapse text-sm', className)}>
        {children}
      </table>
    </div>
  );
}

export function Th({ className, children, ...rest }: ComponentProps<'th'>) {
  return (
    <th
      {...rest}
      className={cn('border-b border-line px-3 py-2.5 text-left text-xs font-medium whitespace-nowrap text-muted', className)}
    >
      {children}
    </th>
  );
}

export function Td({ className, children, ...rest }: ComponentProps<'td'>) {
  return (
    <td {...rest} className={cn('border-b border-line px-3 py-2.5 align-top text-ink', className)}>
      {children}
    </td>
  );
}

export function Tr({ className, children, ...rest }: ComponentProps<'tr'>) {
  return (
    <tr {...rest} className={cn('transition-colors hover:bg-accent-soft/40', className)}>
      {children}
    </tr>
  );
}

/** Column header that toggles sorting; `active` shows the current direction. */
export function SortableTh({
  label,
  active,
  order,
  onSort,
  className,
}: {
  label: ReactNode;
  active: boolean;
  order: 'asc' | 'desc';
  onSort: () => void;
  className?: string;
}) {
  return (
    <Th className={className} aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={onSort} className="inline-flex items-center gap-1 hover:text-ink">
        {label}
        <span aria-hidden className={cn('text-[10px]', active ? 'text-ink' : 'text-transparent')}>
          {order === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </Th>
  );
}
